import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = "What to Do When a Donor's Card Expires — Before It Silently Cancels Their Gift";
const DESCRIPTION =
    "A practical guide to handling expired or declined donor cards — why recurring gifts quietly stop without anyone deciding to stop giving, how to catch it early, and a short, low-pressure message that gets the gift restarted.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover2.jpg',
            datePublished: '2026-07-30',
            dateModified: '2026-07-30',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/what-to-do-when-a-donors-card-expires',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: "Why do recurring donations stop even when a donor didn't mean to cancel?",
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "The most common reason is an expired or reissued card — banks send new card numbers for fraud protection, renewal, or a lost card, and the recurring charge quietly fails with nobody actively deciding to stop giving.",
                    },
                },
                {
                    '@type': 'Question',
                    name: "How will I know if a donor's card has expired?",
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "A platform that processes recurring gifts should flag a failed charge to you directly rather than leaving you to notice a lower total weeks later. Check your dashboard for failed-payment notices regularly instead of only reviewing totals at the end of the month.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What should I say to a donor whose card failed?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Keep it short, factual, and free of guilt: mention that the card on file didn't go through, that it happens all the time, and include a direct link to update it — without implying they chose to stop giving.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How quickly should I follow up on a failed recurring gift?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Within a week or two of the failed charge. The longer a gift stays lapsed, the more it fades into a donor's normal routine as a form of giving they've simply stopped doing, rather than a card update they still intend to make.",
                    },
                },
            ],
        },
    ],
};

export default function WhatToDoWhenADonorsCardExpires() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/what-to-do-when-a-donors-card-expires' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-card-expires-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover2.jpg" alt="A hand updating payment details on a phone" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Not every lapsed donor decided to stop giving. A large share of quiet drop-offs in recurring support have nothing to do with a change of heart — a bank reissues a card, a subscription renews with a new expiration date, or a fraud flag blocks a charge, and a monthly gift simply stops with nobody actively choosing to end it. Left unaddressed, this looks exactly like lost support. Caught early, it's usually a two-minute fix.</p>

                    <h2>Why this matters more than it seems</h2>

                    <p>A donor who meant to keep giving but whose card quietly failed is one of the easiest "wins" in support raising — you don't need to convince them of anything, you just need to tell them and make it easy to fix. Compare that to the effort of finding and onboarding a brand-new supporter, and a five-minute follow-up on a failed card is often the highest-leverage thing you can do in a given week.</p>

                    <h2>Common reasons a card stops working</h2>

                    <ul>
                        <li><strong>Expiration:</strong> the card on file simply reached its expiration date.</li>
                        <li><strong>Reissue:</strong> the bank sent a new card number after a data breach, upgrade, or lost/stolen report — even though the account itself is unchanged.</li>
                        <li><strong>Insufficient funds:</strong> a one-time timing issue, not a sign the donor can no longer afford the gift.</li>
                        <li><strong>Fraud flags:</strong> the bank blocks a recurring charge it doesn't recognize, especially after the donor travels or makes an unusual purchase elsewhere.</li>
                    </ul>

                    <p>None of these reflect a donor's intent. Treating them as if they might is exactly the mistake to avoid.</p>

                    <h2>Catch it early instead of noticing it late</h2>

                    <p>The worst way to discover a failed recurring gift is a lower total at the end of the month, long after the failure happened. Check your giving dashboard for failed-payment notices on a regular basis — weekly is reasonable — so a lapsed gift gets a follow-up within days, not discovered by accident weeks later. See our guide on <a href="/resources/donor-retention-for-missionaries">stopping supporters from quietly lapsing</a> for the broader habit this fits into.</p>

                    <h2>A short, guilt-free message</h2>

                    <p>"Hey [name] — just a heads up that the card on file for your monthly gift didn't go through this month (this happens all the time when banks reissue cards, nothing you did wrong!). Here's a quick link to update it if you'd like to keep it going: [link]. Either way, thank you for everything you've already given."</p>

                    <p>Three things make this work: it states a fact instead of implying a choice, it removes any guilt with "nothing you did wrong," and it gives a direct link so fixing it takes seconds rather than a login-and-search.</p>

                    <h2>Don't wait for a big batch to follow up</h2>

                    <p>It's tempting to let a few failed payments pile up and send one round of reminders later. The longer a lapsed gift sits, the more it settles into a donor's routine as "something I used to give to," rather than a card update they still mean to make. A quick note within a week or two, sent individually, keeps the fix feeling small instead of overdue.</p>

                    <h2>Make the fix itself effortless</h2>

                    <p>The message only works if updating a card is genuinely fast. A direct link to a donor's existing recurring gift — where they can enter a new card in a few taps rather than creating a new gift from scratch — is what turns "I meant to fix that" into an actual restarted gift. On <a href="/">Devotion Fund</a>, a supporter updates their own card without needing you in the loop at all.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>Why do recurring gifts stop even when a donor didn't mean to cancel?</strong><br />
                    Usually an expired or reissued card causes a silent failed charge, not a change of heart.</p>

                    <p><strong>How will I know a card has expired?</strong><br />
                    Check your platform's failed-payment notices regularly instead of only reviewing totals at month's end.</p>

                    <p><strong>What should I say to a donor whose card failed?</strong><br />
                    Keep it short and guilt-free — state the fact, remove blame, and include a direct link to update it.</p>

                    <p><strong>How quickly should I follow up?</strong><br />
                    Within a week or two, before the lapsed gift settles into the donor's routine as something they've simply stopped doing.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/donor-retention-for-missionaries">Donor Retention for Missionaries: How to Stop Supporters From Quietly Lapsing</a></li>
                        <li><a href="/resources/what-to-do-when-a-major-donor-stops-giving">What to Do When a Major Donor Stops Giving</a></li>
                        <li><a href="/resources/how-to-ask-a-one-time-donor-to-become-a-monthly-supporter">How to Ask a One-Time Donor to Become a Monthly Supporter</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We built failed-payment alerts into the platform after hearing the same story too many times: a missionary assuming a donor had quietly walked away, when a bank had simply reissued a card months earlier.</p>
                    <p><small><em>Last updated: 2026-07-30.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/what-to-do-when-a-donors-card-expires" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
